import { CalendarDays, Eye, Search } from "lucide-react";
import TablePagination from "../Utils/TablePagination";

// =====================================================
// SECTION INFO
// =====================================================

const SectionInfo = ({ title, description, dark = false, className = "" }) => {
  return (
    <div className={`group relative shrink-0 ${className}`}>
      <button
        type="button"
        aria-label={`About ${title}`}
        className={`flex h-7 w-7 items-center justify-center rounded-md border transition ${
          dark
            ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
            : "border-gray-200 bg-white text-gray-500 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600"
        }`}
      >
        <Eye size={13} />
      </button>

      <div className="pointer-events-none invisible absolute right-0 top-full z-[150] mt-1.5 w-[290px] max-w-[calc(100vw-24px)] translate-y-1 rounded-lg border border-gray-200 bg-white p-2.5 text-left opacity-0 shadow-xl transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <p className="text-xs font-black text-gray-900">{title}</p>

        <p className="mt-1 text-[10px] leading-4 text-gray-600">
          {description}
        </p>
      </div>
    </div>
  );
};

// =====================================================
// WEEKLY DRIVER RECORDS TABLE
// =====================================================

const WeeklyDriverRecordsTable = ({
  filteredRows = [],
  driverSummary = [],

  search,
  setSearch,

  currentPage,
  setCurrentPage,

  weeklyDriverReportLoading,
  reportData = [],

  currentRows = [],
  startIndex,

  totalCount,
  rowsPerPage,

  formatCurrency,
}) => {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* =================================================
          HEADER
      ================================================= */}

      <div className="flex flex-col gap-2 border-b border-gray-100 p-2.5 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-sm font-black text-gray-900">
            Weekly Driver Records
          </h3>

          <p className="mt-0.5 text-[10px] leading-4 text-gray-500">
            {filteredRows.length} records • {driverSummary.length} drivers •
            full API amount breakdown
          </p>
        </div>

        {/* =================================================
            SEARCH
        ================================================= */}

        <div className="flex w-full items-center gap-2 md:w-auto">
          <SectionInfo
            title="Weekly Driver Records"
            description="Raw driver-week audit table built from the weekly API. It shows driver identity, week range, task counts, derived Pending / Other, Success/Decline rates, each amount field separately and the combined Reported Total. Search filters these rows only."
          />

          <div className="relative w-full md:w-64">
            <Search
              size={15}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search driver, code, team, phone or week..."
              className="h-8 w-full rounded-lg border border-gray-200 pl-8 pr-2 text-[11px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>
        </div>
      </div>

      {/* =================================================
          REFRESH LOADING
      ================================================= */}

      {weeklyDriverReportLoading && reportData.length > 0 && (
        <div className="border-b border-orange-100 bg-orange-50 px-3 py-1.5 text-[10px] font-semibold text-orange-700">
          Refreshing weekly report...
        </div>
      )}

      {/* =================================================
          EMPTY STATE
      ================================================= */}

      {currentRows.length === 0 ? (
        <div className="flex min-h-[110px] flex-col items-center justify-center text-center">
          <CalendarDays className="mb-3 h-9 w-9 text-gray-300" />

          <p className="text-sm font-bold text-gray-700">
            No weekly report found
          </p>

          <p className="mt-1 text-xs text-gray-500">
            Change the date range and apply the filter.
          </p>
        </div>
      ) : (
        <>
          {/* =================================================
              TABLE
          ================================================= */}

          <div className="overflow-x-auto">
            <table className="min-w-[1320px] w-full text-[10px] leading-tight">
              <thead className="bg-orange-400 text-left text-white">
                <tr>
                  <th className="px-2 py-1.5">#</th>

                  <th className="px-2 py-1.5">Driver</th>

                  <th className="px-2 py-1.5">Team / Contact</th>

                  <th className="px-2 py-1.5">Week</th>

                  <th className="px-2 py-1.5 text-right">Assigned</th>

                  <th className="px-2 py-1.5 text-right">Successful</th>

                  <th className="px-2 py-1.5 text-right">Declined</th>

                  <th className="px-2 py-1.5 text-right">Pending / Other</th>

                  <th className="px-2 py-1.5 text-right">Success Rate</th>

                  <th className="px-2 py-1.5 text-right">Decline Rate</th>

                  <th className="px-2 py-1.5 text-right">Week Amount</th>

                  <th className="px-2 py-1.5 text-right">Per Order</th>

                  <th className="px-2 py-1.5 text-right">Extra</th>

                  <th className="px-2 py-1.5 text-right">Reported Total</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {currentRows.map((row, index) => (
                  <tr key={row.id} className="transition hover:bg-orange-50/40">
                    {/* =================================================
                        SERIAL
                    ================================================= */}

                    <td className="px-2 py-1.5 text-gray-500">
                      {startIndex + index + 1}
                    </td>

                    {/* =================================================
                        DRIVER
                    ================================================= */}

                    <td className="px-2 py-1.5">
                      <p className="font-black text-gray-900">
                        {row.driverName}
                      </p>

                      <p className="text-[9px] text-gray-500">
                        {row.driverCode}
                      </p>
                    </td>

                    {/* =================================================
                        TEAM / CONTACT
                    ================================================= */}

                    <td className="px-2 py-1.5">
                      <p className="font-semibold text-gray-700">
                        {row.teamName}
                      </p>

                      <p className="text-[9px] text-gray-500">{row.phone}</p>
                    </td>

                    {/* =================================================
                        WEEK
                    ================================================= */}

                    <td className="whitespace-nowrap px-2 py-1.5 text-gray-600">
                      {row.weekStart} → {row.weekEnd}
                    </td>

                    {/* =================================================
                        ASSIGNED
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right font-black text-gray-900">
                      {row.assignedTasks}
                    </td>

                    {/* =================================================
                        SUCCESS
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right font-black text-emerald-700">
                      {row.successfulTasks}
                    </td>

                    {/* =================================================
                        DECLINED
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right font-black text-red-700">
                      {row.declinedTasks}
                    </td>

                    {/* =================================================
                        PENDING
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right font-black text-amber-700">
                      {row.pendingTasks}
                    </td>

                    {/* =================================================
                        SUCCESS RATE
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 font-black ${
                          row.successRate >= 90
                            ? "bg-emerald-50 text-emerald-700"
                            : row.successRate >= 75
                              ? "bg-blue-50 text-blue-700"
                              : row.successRate >= 60
                                ? "bg-amber-50 text-amber-700"
                                : "bg-red-50 text-red-700"
                        }`}
                      >
                        {row.successRate.toFixed(1)}%
                      </span>
                    </td>

                    {/* =================================================
                        DECLINE RATE
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 font-black ${
                          row.declineRate >= 25
                            ? "bg-red-50 text-red-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {row.declineRate.toFixed(1)}%
                      </span>
                    </td>

                    {/* =================================================
                        WEEK AMOUNT
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right text-gray-700">
                      {formatCurrency(row.totalWeekAmount)}
                    </td>

                    {/* =================================================
                        PER ORDER
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right text-gray-700">
                      {formatCurrency(row.totalPerOrderAmount)}
                    </td>

                    {/* =================================================
                        EXTRA
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right text-gray-700">
                      {formatCurrency(row.totalExtraAmount)}
                    </td>

                    {/* =================================================
                        REPORTED TOTAL
                    ================================================= */}

                    <td className="px-2 py-1.5 text-right font-black text-gray-900">
                      {formatCurrency(row.reportedAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          {totalCount > rowsPerPage && (
            <div className="border-t border-gray-100 p-2">
              <TablePagination
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={rowsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WeeklyDriverRecordsTable;
